import type { ListPillItem } from "@search/api/types";
import {
  FilterValue,
  IBaseFilter,
} from "@search/store/filters/base/BaseFilter";

import { toSnakeCase } from "@/utils/helpers";

type SerializedBaseList = Record<string, Array<FilterValue>>;

type BaseListFilterType = {
  defaultValue?: Array<FilterValue>;
  label?: string;
  name: string;
  value: Array<FilterValue>;
};

interface IBaseListFilter
  extends Omit<
    IBaseFilter,
    "get" | "remove" | "serialize" | "set" | "toPills" | "unset" | "value"
  > {
  add: (value: FilterValue) => SerializedBaseList;
  get: () => Array<FilterValue>;
  includes: (value: FilterValue) => boolean;
  remove: (value: FilterValue) => SerializedBaseList;
  serialize: () => SerializedBaseList;
  set: (value: Array<FilterValue>) => SerializedBaseList;
  toPills: () => Array<ListPillItem>;
  unset: () => SerializedBaseList;
  value: Array<FilterValue>;
}

class BaseListFilter implements IBaseListFilter {
  private _defaultValue: Array<FilterValue>;
  private _label: string;
  private _name: string;

  protected _initialValue: Array<FilterValue>;
  protected _value: Array<FilterValue>;

  constructor({
    defaultValue = [],
    name = "baseList",
    label = name,
    value,
  }: BaseListFilterType) {
    this._defaultValue = defaultValue;
    this._initialValue = value;
    this._label = label;
    this._name = name;
    this._value = value;
  }

  get defaultValue() {
    return this._defaultValue;
  }

  get initialValue() {
    return this._initialValue;
  }

  get label() {
    return this._label;
  }

  get name() {
    return this._name;
  }

  get value() {
    return this._value;
  }

  add(value: FilterValue) {
    return this.set([...this.value, value]);
  }

  get() {
    return this.value;
  }

  includes(value: FilterValue) {
    return this.value.includes(value);
  }

  isSet() {
    return this._value.length > 0;
  }

  remove(value: FilterValue) {
    return this.set(this.value.filter((val) => val !== value));
  }

  serialize() {
    return { [this.name]: this.value };
  }

  set(value: Array<FilterValue>) {
    this._value = value;

    return this.serialize();
  }

  toParam() {
    return this._value.map((value) => value.toString()).join(",");
  }

  toPills(): Array<ListPillItem> {
    return this.value.map((value) => ({
      key: value,
      name: this.name,
    })) as Array<ListPillItem>;
  }

  toURL(): Record<string, string> {
    if (this.isSet()) {
      return { [toSnakeCase(this._name)]: this.toParam() };
    }

    return {};
  }

  unset(): SerializedBaseList {
    return this.set(this._defaultValue);
  }
}

export default BaseListFilter;
export { BaseListFilter };
export type { BaseListFilterType, IBaseListFilter };
